/**
 * AI Settings Component
 *
 * Admin interface for configuring AI model provider (Gemini/Kimi).
 */

import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/authAPI';

type AIProvider = 'gemini' | 'kimi';

interface AIProviderInfo {
  id: AIProvider;
  name: string;
  available: boolean;
}

export const AISettings: React.FC = () => {
  const [currentProvider, setCurrentProvider] = useState<AIProvider>('gemini');
  const [providers, setProviders] = useState<AIProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/ai/provider');
      setCurrentProvider(res.data.current);
      setProviders(res.data.providers);
    } catch (e) {
      setError(`Failed to load AI settings: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  const handleProviderChange = async (provider: AIProvider) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.put('/ai/provider', { provider });
      setCurrentProvider(res.data.current);
      setSuccess(`AI provider changed to ${provider}`);
    } catch (e: any) {
      setError(e.response?.data?.error || `Failed to change provider: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card-editorial rounded-2xl p-8">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const activeProvider = providers.find((p) => p.id === currentProvider);
  const geminiProvider = providers.find((p) => p.id === 'gemini');
  const kimiProvider = providers.find((p) => p.id === 'kimi');

  return (
    <div className="card-editorial rounded-2xl p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-stone-100">AI Model Configuration</h2>
          <p className="text-stone-500 text-sm mt-1">
            Select AI provider for audio analysis and beat detection
          </p>
        </div>
        {activeProvider && (
          <div className="flex items-center gap-2">
            <span className="text-stone-500 text-sm">Active:</span>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium border ${
                activeProvider.available
                  ? 'bg-sage-500/10 text-sage-400 border-sage-500/30'
                  : 'bg-coral-500/10 text-coral-400 border-coral-500/30'
              }`}
            >
              {activeProvider.name}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 bg-coral-400/10 border border-coral-400/30 rounded-lg text-coral-400 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 px-4 py-3 bg-sage-400/10 border border-sage-400/30 rounded-lg text-sage-400 text-sm">
          {success}
        </div>
      )}

      {/* Provider Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Gemini Option */}
        <button
          type="button"
          onClick={() => handleProviderChange('gemini')}
          disabled={saving || currentProvider === 'gemini'}
          className={`relative p-5 rounded-xl border-2 text-left transition-all ${
            currentProvider === 'gemini'
              ? 'border-amber-500 bg-amber-500/10'
              : 'border-studio-700 hover:border-studio-600 bg-studio-800/50'
          } ${!geminiProvider?.available ? 'opacity-60' : ''}`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/30">
                <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-stone-100">Google Gemini</h4>
                <p className="text-stone-500 text-xs">gemini-2.0-flash</p>
              </div>
            </div>
            {currentProvider === 'gemini' && (
              <span className="px-2 py-1 bg-amber-500 text-studio-950 text-xs font-bold rounded-full">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span
              className={`w-2 h-2 rounded-full ${
                geminiProvider?.available ? 'bg-sage-500' : 'bg-coral-500'
              }`}
            />
            <span className={geminiProvider?.available ? 'text-sage-400' : 'text-coral-400'}>
              {geminiProvider?.available ? 'Available' : 'API Key Missing'}
            </span>
          </div>
        </button>

        {/* Kimi Option */}
        <button
          type="button"
          onClick={() => handleProviderChange('kimi')}
          disabled={saving || currentProvider === 'kimi' || !kimiProvider?.available}
          className={`relative p-5 rounded-xl border-2 text-left transition-all ${
            currentProvider === 'kimi'
              ? 'border-amber-500 bg-amber-500/10'
              : 'border-studio-700 hover:border-studio-600 bg-studio-800/50'
          } ${!kimiProvider?.available ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center border border-purple-500/30">
                <svg className="w-5 h-5 text-purple-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-stone-100">Moonshot Kimi</h4>
                <p className="text-stone-500 text-xs">moonshot-v1-8k</p>
              </div>
            </div>
            {currentProvider === 'kimi' && (
              <span className="px-2 py-1 bg-amber-500 text-studio-950 text-xs font-bold rounded-full">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span
              className={`w-2 h-2 rounded-full ${
                kimiProvider?.available ? 'bg-sage-500' : 'bg-coral-500'
              }`}
            />
            <span className={kimiProvider?.available ? 'text-sage-400' : 'text-coral-400'}>
              {kimiProvider?.available ? 'Available' : 'API Key Missing'}
            </span>
          </div>
        </button>
      </div>

      {/* API Configuration Info */}
      <div className="mt-6 border border-studio-800 rounded-xl p-5 bg-studio-900/50">
        <h3 className="text-sm font-semibold text-stone-300 mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          API Configuration
        </h3>
        <div className="space-y-3 text-sm text-stone-400">
          <p>
            Configure API keys via environment variables:
          </p>
          <div className="bg-studio-950 rounded-lg p-4 font-mono text-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-stone-300">GEMINI_API_KEY=...</span>
              <span className={geminiProvider?.available ? 'text-sage-400' : 'text-coral-400'}>
                {geminiProvider?.available ? '✓ Set' : '✗ Missing'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone-300">KIMI_API_KEY=...</span>
              <span className={kimiProvider?.available ? 'text-sage-400' : 'text-coral-400'}>
                {kimiProvider?.available ? '✓ Set' : '✗ Missing'}
              </span>
            </div>
          </div>
          <p className="text-xs text-stone-500">
            If AI fails, the system automatically falls back to algorithmic beat detection.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AISettings;
